// ============================================================================
// 標高データ取得 / 国土地理院 標高タイル (dem_png)
// ----------------------------------------------------------------------------
// PLATEAUと違い全国をシームレスにカバーするタイル。ズーム14のPNGタイルを
// 必要範囲ぶんフェッチし、1枚のFloat32Arrayラスタへモザイク合成する。
// デコード式・CORS許可は事前にNodeスクリプトで実測検証済み
// （富士山頂 ≈3754m / 東京タワー付近 ≈20.6m / 隅田川 ≈2.4m、
//   Origin付きリクエストで Access-Control-Allow-Origin: * を確認）。
//
// タイル座標変換は標準的な Web Mercator (slippy map) の式。
// 標高デコードは地理院公式仕様 (https://maps.gsi.go.jp/development/demtile.html):
//   x = 2^16*R + 2^8*G + B
//   x < 2^23        → h = x * 0.01
//   x == 2^23       → 無効値（データなし。海など）
//   x > 2^23        → h = (x - 2^24) * 0.01
// ============================================================================

const TILE_SIZE = 256;
const NO_DATA = 2 ** 23; // = 8388608

export type DemLayer = "dem_png" | "dem5a_png" | "dem5b_png" | "dem5c_png";

export interface ElevationRaster {
  /** 左上(北西)角の経緯度。 */
  west: number;
  north: number;
  /** 右下(南東)角の経緯度。 */
  east: number;
  south: number;
  cols: number;
  rows: number;
  /** 1ピクセルあたりの経度・緯度の大きさ(度, 正の値)。 */
  pixelDegLng: number;
  pixelDegLat: number;
  /** row-major, row0=北端。NaN = データなし。 */
  data: Float32Array;
}

function lngToTileX(lng: number, z: number): number {
  return ((lng + 180) / 360) * 2 ** z;
}
function latToTileY(lat: number, z: number): number {
  const latRad = (lat * Math.PI) / 180;
  return (
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * 2 ** z
  );
}
function tileToLng(x: number, z: number): number {
  return (x / 2 ** z) * 360 - 180;
}
function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/** RGB から標高(m)を求める。無効値(海・データ無し)は null。 */
export function decodeElevation(r: number, g: number, b: number): number | null {
  const x = r * 65536 + g * 256 + b;
  if (x === NO_DATA) return null;
  if (x < NO_DATA) return x * 0.01;
  return (x - 16777216) * 0.01;
}

const tileCache = new Map<string, Promise<Float32Array | null>>();

/** キャッシュ済みDEMタイルを破棄する。打ち上げ地点を変えて計算し直す際、前回地点の
 *  タイルを持ち続けないようにするために呼ぶ（無制限にメモリへ積み上がるのを防ぐ）。 */
export function clearDemCache(): void {
  tileCache.clear();
}

function decodeTilePixels(bitmap: ImageBitmap): Float32Array {
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context を取得できません");
  ctx.drawImage(bitmap, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  const out = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    const e = decodeElevation(data[o], data[o + 1], data[o + 2]);
    out[i] = e === null ? NaN : e;
  }
  return out;
}

/** 1タイル分の標高を取得（キャッシュ済みならそれを返す）。取得不可なら null。 */
export function fetchDemTile(
  z: number,
  x: number,
  y: number,
  layer: DemLayer = "dem_png"
): Promise<Float32Array | null> {
  const key = `${layer}/${z}/${x}/${y}`;
  const cached = tileCache.get(key);
  if (cached) return cached;

  const promise = (async () => {
    const url = `https://cyberjapandata.gsi.go.jp/xyz/${layer}/${z}/${x}/${y}.png`;
    try {
      const res = await fetch(url);
      if (!res.ok) return null; // 範囲外(海上など)は404になりうる
      const blob = await res.blob();
      const bitmap = await createImageBitmap(blob);
      const pixels = decodeTilePixels(bitmap);
      bitmap.close?.();
      return pixels;
    } catch {
      return null;
    }
  })();
  tileCache.set(key, promise);
  return promise;
}

/**
 * bbox [west, south, east, north] を覆うタイル群を取得し、1枚のラスタにモザイク合成する。
 */
export async function buildElevationRaster(
  bbox: [number, number, number, number],
  zoom: number,
  layer: DemLayer = "dem_png",
  onProgress?: (done: number, total: number) => void
): Promise<ElevationRaster> {
  const [west, south, east, north] = bbox;
  const x0 = Math.floor(lngToTileX(west, zoom));
  const x1 = Math.floor(lngToTileX(east, zoom));
  const y0 = Math.floor(latToTileY(north, zoom));
  const y1 = Math.floor(latToTileY(south, zoom));

  const cols = (x1 - x0 + 1) * TILE_SIZE;
  const rows = (y1 - y0 + 1) * TILE_SIZE;
  const data = new Float32Array(cols * rows).fill(NaN);

  const tileCoords: { tx: number; ty: number }[] = [];
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) tileCoords.push({ tx, ty });
  }

  let done = 0;
  let nextIdx = 0;
  const CONCURRENCY = 8;

  async function worker() {
    while (nextIdx < tileCoords.length) {
      const i = nextIdx++;
      const { tx, ty } = tileCoords[i];
      const tile = await fetchDemTile(zoom, tx, ty, layer);
      if (tile) {
        const ox = (tx - x0) * TILE_SIZE;
        const oy = (ty - y0) * TILE_SIZE;
        for (let py = 0; py < TILE_SIZE; py++) {
          const srcOff = py * TILE_SIZE;
          const dstOff = (oy + py) * cols + ox;
          data.set(tile.subarray(srcOff, srcOff + TILE_SIZE), dstOff);
        }
      }
      done++;
      onProgress?.(done, tileCoords.length);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, tileCoords.length) }, worker)
  );

  const rasterWest = tileToLng(x0, zoom);
  const rasterEast = tileToLng(x1 + 1, zoom);
  const rasterNorth = tileToLat(y0, zoom);
  const rasterSouth = tileToLat(y1 + 1, zoom);

  return {
    west: rasterWest,
    east: rasterEast,
    north: rasterNorth,
    south: rasterSouth,
    cols,
    rows,
    pixelDegLng: (rasterEast - rasterWest) / cols,
    pixelDegLat: (rasterNorth - rasterSouth) / rows,
    data,
  };
}

/**
 * 解析半径(m)に応じて、タイル枚数を抑えつつ十分な解像度になるズームを選ぶ。
 * 半径が大きいほど粗く（=タイル枚数をほぼ一定に保つ）。
 */
export function pickDemZoom(radiusM: number): number {
  if (radiusM <= 6000) return 14;
  if (radiusM <= 12000) return 13;
  if (radiusM <= 20000) return 12;
  return 11;
}

/** ラスタのピクセル値。データ無し(NaN)は0mとして扱う。 */
function pixelAt(raster: ElevationRaster, x: number, y: number): number {
  const v = raster.data[y * raster.cols + x];
  return Number.isNaN(v) ? 0 : v;
}

/** ラスタから任意の緯度経度の標高を双線形補間で求める。データ無し(NaN)は0mとして扱う。 */
export function sampleElevation(
  raster: ElevationRaster,
  lng: number,
  lat: number
): number {
  const fx = (lng - raster.west) / raster.pixelDegLng;
  const fy = (raster.north - lat) / raster.pixelDegLat;
  const cx = Math.min(Math.max(fx, 0), raster.cols - 1.001);
  const cy = Math.min(Math.max(fy, 0), raster.rows - 1.001);
  const x0 = Math.floor(cx);
  const y0 = Math.floor(cy);
  const tx = cx - x0;
  const ty = cy - y0;

  const top = pixelAt(raster, x0, y0) * (1 - tx) + pixelAt(raster, x0 + 1, y0) * tx;
  const bottom =
    pixelAt(raster, x0, y0 + 1) * (1 - tx) + pixelAt(raster, x0 + 1, y0 + 1) * tx;
  return top * (1 - ty) + bottom * ty;
}
